// Блокировка приложения на этом устройстве: состояние в localStorage (переживает перезагрузку,
// общее для вкладок). Сервер блокирует сессию сам (423), здесь — то, что нужно без сети:
//   lockAfter   — через сколько секунд без дела блокировать (выбор устройства, уходит при входе);
//   lastActive  — когда приложение последний раз было открыто на экране;
//   locked      — заблокировано ли сейчас;
//   passkeyId   — ключ доступа, добавленный на этом устройстве (есть — показываем «отпечаток»);
//   unlockOptions — вызов разблокировки, взятый у сервера заранее (для разблокировки без сети);
//   pendingUnlock — ответ ключа, полученный без сети: отправится на сервер, когда появится связь.

import { useSyncExternalStore } from 'react'
import { getActiveUserId, registerIdentityStorage, subscribeIdentity } from '@/api/identity'

export const LOCK_CHOICES = [60, 300, 900, 1800, 3600] as const

const KEYS = {
  lockAfter: 'hs-lock-after',
  lastActive: 'hs-last-active',
  locked: 'hs-locked',
  passkeyId: 'hs-passkey-id',
  unlockOptions: 'hs-unlock-options',
  pendingUnlock: 'hs-pending-unlock',
} as const

export type StoredOptions = { options: unknown; fetchedAt: number }

function read(key: string): string | null {
  try {
    const owner = getActiveUserId()
    return owner ? localStorage.getItem(`${key}:${owner}`) : null
  } catch {
    return null
  }
}

/** false — localStorage недоступен (приватный режим): блокировка держится в памяти вкладки. */
function write(key: string, value: string | null): boolean {
  try {
    const owner = getActiveUserId()
    if (!owner) return false
    if (value === null) localStorage.removeItem(`${key}:${owner}`)
    else localStorage.setItem(`${key}:${owner}`, value)
    return true
  } catch {
    return false
  }
}

function readJson<T>(key: string): T | null {
  const raw = read(key)
  if (!raw) return null
  try {
    return JSON.parse(raw) as T
  } catch {
    return null
  }
}

// --- подписка --------------------------------------------------------------------------------

type Listener = () => void
const listeners = new Set<Listener>()
let memoryLocked = false

function notify(): void {
  for (const listener of listeners) listener()
}

function subscribe(listener: Listener): () => void {
  listeners.add(listener)
  const onStorage = (event: StorageEvent) => {
    if (event.key === null || Object.values(KEYS).some((key) => event.key?.startsWith(`${key}:`))) listener()
  }
  window.addEventListener('storage', onStorage)
  return () => {
    listeners.delete(listener)
    window.removeEventListener('storage', onStorage)
  }
}

// --- чтение и запись -------------------------------------------------------------------------

export function getLockAfter(): number | null {
  const value = Number(read(KEYS.lockAfter))
  return Number.isFinite(value) && value > 0 ? value : null
}

export function setLockAfter(seconds: number | null): void {
  if (seconds === getLockAfter()) return
  write(KEYS.lockAfter, seconds === null ? null : String(seconds))
  if (seconds === null) setLocked(false)
  markActive()
  notify()
}

let storageWorks = true

export function isLocked(): boolean {
  return Boolean(getActiveUserId()) && (storageWorks ? read(KEYS.locked) === '1' : memoryLocked)
}

export function setLocked(value: boolean): void {
  if (value === isLocked()) return
  memoryLocked = value
  storageWorks = write(KEYS.locked, value ? '1' : null)
  if (!value) markActive()
  notify()
}

export function markActive(now = Date.now()): void {
  write(KEYS.lastActive, String(now))
}

/** Пора ли заблокировать: блокировка включена и приложение не было на экране дольше срока. */
export function isOverdue(now = Date.now()): boolean {
  const after = getLockAfter()
  if (after === null) return false
  const last = Number(read(KEYS.lastActive))
  if (!Number.isFinite(last) || last <= 0) return true
  return now - last >= after * 1000
}

/** Запрос идёт от открытого на экране и разблокированного приложения (заголовок активности). */
export function isAppActive(): boolean {
  return getLockAfter() !== null && document.visibilityState === 'visible' && !isLocked()
}

export function getPasskeyId(): string | null {
  return read(KEYS.passkeyId)
}

export function setPasskeyId(id: string | null): void {
  write(KEYS.passkeyId, id)
  notify()
}

export function getUnlockOptions(): StoredOptions | null {
  return readJson<StoredOptions>(KEYS.unlockOptions)
}

export function setUnlockOptions(value: StoredOptions | null): void {
  write(KEYS.unlockOptions, value ? JSON.stringify(value) : null)
}

export function getPendingUnlock(): unknown {
  return readJson<unknown>(KEYS.pendingUnlock)
}

export function setPendingUnlock(value: unknown): void {
  write(KEYS.pendingUnlock, value ? JSON.stringify(value) : null)
}

/** Выход: вызов и ответ ключа принадлежат закрытой сессии. Выбор блокировки и ключ остаются. */
export function clearSessionLockState(): void {
  memoryLocked = false
  write(KEYS.locked, null)
  setUnlockOptions(null)
  setPendingUnlock(null)
  notify()
}

// --- React -----------------------------------------------------------------------------------

export type LockSnapshot = { locked: boolean; lockAfter: number | null; passkeyId: string | null }

let snapshot: LockSnapshot = { locked: false, lockAfter: null, passkeyId: null }

function getSnapshot(): LockSnapshot {
  const next = { locked: isLocked(), lockAfter: getLockAfter(), passkeyId: getPasskeyId() }
  if (
    next.locked !== snapshot.locked ||
    next.lockAfter !== snapshot.lockAfter ||
    next.passkeyId !== snapshot.passkeyId
  ) {
    snapshot = next
  }
  return snapshot
}

export function useLockState(): LockSnapshot {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
}

/** Для тестов: сбросить состояние памяти. */
export function resetLockMemory(): void {
  memoryLocked = false
  storageWorks = true
  snapshot = { locked: false, lockAfter: null, passkeyId: null }
}

registerIdentityStorage((owner) => {
  if (owner) for (const key of Object.values(KEYS)) {
    try { localStorage.removeItem(`${key}:${owner}`) } catch { /* memory-only browser */ }
  }
  resetLockMemory()
}, (user) => {
  if (!user.legacy_owner) return
  for (const key of Object.values(KEYS)) {
    try {
      const value = localStorage.getItem(key)
      if (value !== null) localStorage.setItem(`${key}:${user.id}`, value)
      localStorage.removeItem(key)
    } catch { /* memory-only browser */ }
  }
})
subscribeIdentity(() => { resetLockMemory(); notify() })
